import { useContext } from 'react';
import type {
  CSSProperties,
  HTMLAttributes,
  ReactElement,
} from 'react';

import styled from 'styled-components';
import type {
  CSSObject,
} from 'styled-components';

import type {
  ThemeType,
} from '@n3/css-base';

import { GridContext } from '../Grid';

import {
  CellValue,
} from './types';

const getStyleVW = (
  cols: number,
  gridTheme: ThemeType['grid'],
): CSSProperties => {
  if (!cols) {
    return {};
  }

  const basis = `${(100 * cols) / gridTheme.COLS_COUNT}vw`;

  return {
    flexBasis: basis,
    maxWidth: basis,
  };
};

const percentBaseWidth = 100 / 12;
const getStylePercent = (
  cols: number,
): CSSProperties => {
  if (!cols) {
    return {};
  }

  const width = `${percentBaseWidth * cols}%`;

  return {
    width,
    maxWidth: width,
    minWidth: width,
  };
};

const getStylePx = (
  cols: number,
  gridTheme: ThemeType['grid'],
): CSSProperties => {
  if (!cols) {
    return {};
  }

  const basis = `${(gridTheme.GRID_MAX_WIDTH * cols) / gridTheme.COLS_COUNT}px`;

  return {
    flexBasis: basis,
    maxWidth: basis,
  };
};

export const StyledCell = styled.div<{
  $isFixedWidth: boolean;
  $isPercenage: boolean;
  $xs?: CellValue;
  $sm?: CellValue;
  $md?: CellValue;
  $lg?: CellValue;
  $xl?: CellValue;
}>(({
  $isFixedWidth,
  $isPercenage,
  $xs = 0,
  $sm = 0,
  $md = 0,
  $lg = 0,
  $xl = 0,

  theme: {
    grid,
  },
}) => {
  const baseStyle: CSSObject = {
    boxSizing: 'border-box',
    paddingLeft: grid.COLS_INDENT_HALF,
    paddingRight: grid.COLS_INDENT_HALF,
  };

  if ($isFixedWidth && !$isPercenage) {
    return {
      ...baseStyle,
      ...getStylePx($xs, grid),
      ...getStylePx($sm, grid),
      ...getStylePx($md, grid),
      ...getStylePx($lg, grid),
      ...getStylePx($xl, grid),
    };
  }

  const getStyle = $isPercenage ? getStylePercent : getStyleVW;

  return {
    ...baseStyle,
    width: '100vw',
    ...getStyle($xs, grid),

    [`@media (min-width: ${grid.SCREEN_WIDTH_SM}px)`]: getStyle($sm, grid),
    [`@media (min-width: ${grid.SCREEN_WIDTH_MD}px)`]: getStyle($md, grid),
    [`@media (min-width: ${grid.SCREEN_WIDTH_LG}px)`]: getStyle($lg, grid),
    [`@media (min-width: ${grid.SCREEN_WIDTH_XL}px)`]: getStyle($xl, grid),

    [`@media (min-width: ${grid.GRID_MAX_WIDTH}px)`]: $isPercenage ? undefined : {
      ...getStylePx($xs, grid),
      ...getStylePx($sm, grid),
      ...getStylePx($md, grid),
      ...getStylePx($lg, grid),
      ...getStylePx($xl, grid),
    },
  };
});

export type CellProps = HTMLAttributes<HTMLDivElement> & {
  /**
   * Пропорциональная ширина колонки относительно контейнера
   */
  isPercenage?: boolean;
  /**
   * Количество колонок в разрешениях до 576px
   */
  xs?: CellValue;
  /**
  * Количество колонок в разрешениях до 768px
  */
  sm?: CellValue;
  /**
  * Количество колонок в разрешениях до 992px
  */
  md?: CellValue;
  /**
  * Количество колонок в разрешениях до 1200px
  */
  lg?: CellValue;
  /**
  * Количество колонок в разрешениях от 1200px включительно
  */
  xl?: CellValue;
};

export function Cell({
  isPercenage = false,
  xs = undefined,
  sm = undefined,
  md = undefined,
  lg = undefined,
  xl = undefined,
  ...rest
}: CellProps): ReactElement {
  const {
    isFixedWidth,
  } = useContext(GridContext);

  return (
    <StyledCell
      $isPercenage={isPercenage}
      $xs={xs}
      $sm={sm}
      $md={md}
      $lg={lg}
      $xl={xl}
      $isFixedWidth={isFixedWidth}
      {...rest}
    />
  );
}
